import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useOrgStore, applyOrgTheme } from '@/stores/orgStore';
import type { OrgTheme } from '@/types/api';
import type { Organization } from '@/types/database.types';
import { isActiveSubscription } from '@/lib/subscriptionStatus';
import {
  ORG_LOGOS_BUCKET,
  buildOrgLogoStoragePath,
  extractOrgLogoStoragePath,
  getOrgLogoMimeType,
  validateOrgLogoFile,
} from '@/lib/orgLogo';

export function useOrg() {
  const { organization, memberRole, isLoading } = useOrgStore();

  const isTrialActive =
    organization?.trial_ends_at && new Date(organization.trial_ends_at) > new Date();
  const isSubscriptionActive = isActiveSubscription(organization?.subscription_status);
  const hasAccess = isSubscriptionActive || isTrialActive;

  return {
    organization,
    memberRole,
    isLoading,
    isTrialActive: !!isTrialActive,
    isSubscriptionActive: !!isSubscriptionActive,
    hasAccess: !!hasAccess,
  };
}

export function useOrgSettings(orgId: string | undefined) {
  return useQuery({
    queryKey: ['org-settings', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

/** Uploads a logo, points the organisation at it, then clears the old file. */
export function useUpdateOrgLogo() {
  const updateSettings = useUpdateOrgSettings();

  return useMutation({
    mutationFn: async ({
      orgId,
      file,
      previousLogoUrl,
    }: {
      orgId: string;
      file: File;
      previousLogoUrl?: string | null;
    }) => {
      const validationError = validateOrgLogoFile(file);
      if (validationError) throw new Error(validationError);

      const mimeType = getOrgLogoMimeType(file);
      if (!mimeType) throw new Error('Upload a supported logo image.');

      const filePath = buildOrgLogoStoragePath(orgId, file);
      const { error: uploadError } = await supabase.storage
        .from(ORG_LOGOS_BUCKET)
        .upload(filePath, file, { cacheControl: '3600', contentType: mimeType, upsert: false });
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage.from(ORG_LOGOS_BUCKET).getPublicUrl(filePath);

      try {
        const organization = await updateSettings.mutateAsync({
          orgId,
          updates: { company_logo_url: publicUrlData.publicUrl },
        });
        await removeStoredOrgLogo(previousLogoUrl, orgId, filePath);
        return organization;
      } catch (error) {
        await supabase.storage.from(ORG_LOGOS_BUCKET).remove([filePath]);
        throw error;
      }
    },
  });
}

export function useRemoveOrgLogo() {
  const updateSettings = useUpdateOrgSettings();

  return useMutation({
    mutationFn: async ({ orgId, logoUrl }: { orgId: string; logoUrl?: string | null }) => {
      const organization = await updateSettings.mutateAsync({
        orgId,
        updates: { company_logo_url: null },
      });
      await removeStoredOrgLogo(logoUrl, orgId, null);
      return organization;
    },
  });
}

/**
 * Only deletes files this organisation owns in the logo bucket, so a legacy URL
 * pointing somewhere else is simply forgotten rather than chased.
 */
async function removeStoredOrgLogo(
  logoUrl: string | null | undefined,
  orgId: string,
  keepPath: string | null,
) {
  const path = extractOrgLogoStoragePath(logoUrl, orgId);
  if (!path || path === keepPath) return;
  await supabase.storage.from(ORG_LOGOS_BUCKET).remove([path]);
}

export function useUpdateOrgSettings() {
  const queryClient = useQueryClient();
  const setOrg = useOrgStore((s) => s.setOrg);
  const memberRole = useOrgStore((s) => s.memberRole);

  return useMutation({
    mutationFn: async ({ orgId, updates }: { orgId: string; updates: Partial<Organization> }) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', orgId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (memberRole) {
        setOrg(data, memberRole);
        applyOrgTheme(data.theme as OrgTheme | null);
      }
      queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
  });
}
